/**
 * Lightweight toast system — a context provider plus a `useToast()` hook.
 *
 * Toasts are queued in state and rendered through a portal in a fixed
 * bottom-right stack. Each toast auto-dismisses after a timeout and animates
 * in/out with motion.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';

type ToastTone = 'success' | 'error' | 'info';
type Toast = { id: number; message: string; tone: ToastTone };

type ToastContextValue = {
  toast: (message: string, tone?: ToastTone) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children?: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, tone: ToastTone = 'success') => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), 3800);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="fixed bottom-5 right-5 z-[120] flex flex-col gap-2.5 w-[min(92vw,22rem)]">
          <AnimatePresence>
            {toasts.map((t) => (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, x: 40, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                onClick={() => dismiss(t.id)}
                className="flex items-center gap-3 rounded-2xl bg-surface-lowest border border-outline-variant/30 shadow-xl px-4 py-3 cursor-pointer"
              >
                <span
                  className={cn(
                    'w-7 h-7 shrink-0 rounded-full flex items-center justify-center',
                    t.tone === 'success' && 'bg-status-published/15 text-status-published',
                    t.tone === 'error' && 'bg-status-failed/15 text-status-failed',
                    t.tone === 'info' && 'bg-primary/15 text-primary',
                  )}
                >
                  {t.tone === 'error' ? (
                    <Icons.Warning size={15} />
                  ) : (
                    <Icons.Check size={15} />
                  )}
                </span>
                <p className="text-sm font-medium text-on-surface flex-1">{t.message}</p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
